// src/components/singalong/renderSingAlongFrame.js
//
// Draws one sing-along frame onto a canvas, for the MP4 export.
//
// It reads the same frameAt() the on-screen player uses, so the video and
// the portal can never disagree about which line is lit.
//
// The logo is the one image drawn. It must be loaded with
// crossOrigin="anonymous" (exportSingAlongVideo does that): an image from
// another origin loaded any other way "taints" the canvas and the browser
// then refuses to record it. If it could not be loaded, pass logo: null and
// the frame is drawn without it.

import { COPYRIGHT_TEXT, frameAt, themeOf, wordsOf } from "../../services/lyricSyncService";

const FONT_STACK =
  '"Fredoka", "Baloo 2", "Arial Rounded MT Bold", "Trebuchet MS", "Segoe UI", system-ui, sans-serif';

const font = (weight, px) => `${weight} ${Math.round(px)}px ${FONT_STACK}`;

/** Breaks a line's words into rows that fit maxWidth. Returns [[{w,k,ww}]]. */
const wrapWords = (ctx, words, maxWidth) => {
  const rows = [];
  let row = [];
  let width = 0;
  const space = ctx.measureText(" ").width;
  words.forEach((w, k) => {
    const ww = ctx.measureText(w).width;
    const add = row.length ? space + ww : ww;
    if (row.length && width + add > maxWidth) {
      rows.push(row);
      row = [];
      width = 0;
    }
    row.push({ w, k, ww });
    width += row.length > 1 ? space + ww : ww;
  });
  if (row.length) rows.push(row);
  return rows;
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawBackground = (ctx, W, H, theme) => {
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, theme.bg[0]);
  g.addColorStop(1, theme.bg[1]);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
};

/** The big faint logo: fits inside 72% × 74% of the frame, centred. */
const drawWatermark = (ctx, W, H, logo, opacity) => {
  if (!logo || !(opacity > 0) || !logo.naturalWidth) return;
  const boxW = W * 0.72;
  const boxH = H * 0.74;
  const scale = Math.min(boxW / logo.naturalWidth, boxH / logo.naturalHeight);
  const w = logo.naturalWidth * scale;
  const h = logo.naturalHeight * scale;
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.drawImage(logo, (W - w) / 2, (H - h) / 2, w, h);
  ctx.restore();
};

const drawDots = (ctx, cx, y, seconds, H, theme) => {
  if (!Number.isFinite(seconds) || seconds > 3.2 || seconds < 0) return;
  const left = Math.ceil(seconds);
  const r = H * 0.014;
  const gap = r * 3.2;
  [3, 2, 1].forEach((n, idx) => {
    ctx.beginPath();
    ctx.arc(cx + (idx - 1) * gap, y, r, 0, Math.PI * 2);
    ctx.fillStyle = left >= n ? theme.lit : theme.dim;
    ctx.fill();
  });
};

/**
 * @param ctx      CanvasRenderingContext2D
 * @param opts     { width, height, parsed, cues, t, themeKey, title,
 *                   logo (HTMLImageElement|null), logoOpacity,
 *                   fade (1 = visible … 0 = black, for the ending) }
 */
export const renderSingAlongFrame = (ctx, opts) => {
  const { fade = 1, width: W, height: H } = opts;
  drawFrame(ctx, opts);
  // The ending: the finished frame goes to black over the fade.
  if (fade < 1) {
    ctx.save();
    ctx.globalAlpha = 1 - Math.max(0, fade);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }
};

const drawFrame = (ctx, opts) => {
  const { width: W, height: H, parsed, cues, t, themeKey, title, logo, logoOpacity = 0 } = opts;
  const theme = themeOf(themeKey);
  const frame = frameAt(parsed, cues, t);

  ctx.textBaseline = "middle";
  drawBackground(ctx, W, H, theme);
  drawWatermark(ctx, W, H, logo, logoOpacity);

  // Title, top left.
  if (title) {
    ctx.font = font(700, H * 0.034);
    ctx.fillStyle = theme.sung;
    ctx.textAlign = "left";
    ctx.fillText(title, W * 0.045, H * 0.07);
  }

  // Copyright, bottom centre.
  ctx.font = font(600, H * 0.024);
  ctx.fillStyle = theme.dim;
  ctx.textAlign = "center";
  ctx.fillText(COPYRIGHT_TEXT, W / 2, H * 0.955);

  if (frame.mode === "empty") return;

  const { page } = frame;
  const maxWidth = W * 0.84;
  const baseSize = H * 0.068;

  // Lay out every line first so the block can be centred vertically.
  const blocks = page.lineIdx.map((i) => {
    const state = frame.lineState[i] || "upcoming";
    const size = state === "active" ? baseSize * 1.08 : baseSize;
    ctx.font = font(state === "active" ? 800 : 700, size);
    const rows = wrapWords(ctx, wordsOf(parsed.lines[i]), maxWidth);
    return { i, state, size, rows, lineH: size * 1.28 };
  });

  const gapBetween = baseSize * 0.42;
  const blockH =
    blocks.reduce((sum, b) => sum + b.rows.length * b.lineH, 0) +
    gapBetween * Math.max(0, blocks.length - 1);

  let y = H * 0.5 - blockH / 2 + H * 0.01;

  // Section pill above the block.
  if (page.label) {
    const label = page.parts > 1 ? `${page.label} · ${page.part}/${page.parts}` : page.label;
    ctx.font = font(800, H * 0.03);
    const tw = ctx.measureText(label).width;
    const ph = H * 0.054;
    const pw = tw + H * 0.06;
    const py = y - ph - H * 0.035;
    ctx.fillStyle = theme.pill;
    roundRect(ctx, W / 2 - pw / 2, py, pw, ph, ph / 2);
    ctx.fill();
    ctx.fillStyle = theme.ink;
    ctx.textAlign = "center";
    ctx.fillText(label, W / 2, py + ph / 2 + 1);
  }

  ctx.textAlign = "left";
  blocks.forEach((b) => {
    ctx.font = font(b.state === "active" ? 800 : 700, b.size);
    const space = ctx.measureText(" ").width;

    // Highlight band behind the active line.
    if (b.state === "active") {
      const bandH = b.rows.length * b.lineH + b.size * 0.25;
      ctx.fillStyle = theme.band;
      roundRect(ctx, W * 0.06, y - b.size * 0.12, W * 0.88, bandH, b.size * 0.35);
      ctx.fill();
    }

    b.rows.forEach((row) => {
      const rowW = row.reduce((s, it, idx) => s + it.ww + (idx ? space : 0), 0);
      let x = W / 2 - rowW / 2;
      const cy = y + b.lineH / 2;
      row.forEach((it) => {
        let color = theme.dim;
        if (b.state === "sung") color = theme.sung;
        if (b.state === "active") color = it.k < (frame.litWords || 0) ? theme.lit : theme.ink;
        // Thin edge so words stay crisp over the watermark.
        ctx.lineWidth = Math.max(2, b.size * 0.06);
        ctx.strokeStyle = theme.edge;
        ctx.strokeText(it.w, x, cy);
        ctx.fillStyle = color;
        ctx.fillText(it.w, x, cy);
        x += it.ww + space;
      });
      y += b.lineH;
    });
    y += gapBetween;
  });

  // Below the block: get-ready / music break / next / the end.
  ctx.textAlign = "center";
  const footY = Math.min(H * 0.86, y + H * 0.05);
  if (frame.mode === "intro" || frame.mode === "break") {
    ctx.font = font(700, H * 0.036);
    ctx.fillStyle = theme.ink;
    ctx.fillText(frame.mode === "intro" ? "Get ready to sing!" : "♪ Music break ♪", W / 2, footY);
    drawDots(ctx, W / 2, footY + H * 0.055, frame.countdown, H, theme);
  } else if (frame.mode === "end") {
    ctx.font = font(800, H * 0.04);
    ctx.fillStyle = theme.lit;
    ctx.fillText("⭐ Great singing! ⭐", W / 2, footY);
  } else if (frame.nextPreview) {
    ctx.font = font(600, H * 0.03);
    ctx.fillStyle = theme.dim;
    const text = `Next${frame.nextPreview.label ? ` · ${frame.nextPreview.label}` : ""}:  ${
      frame.nextPreview.text
    }`;
    const clipped = text.length > 70 ? `${text.slice(0, 68)}…` : text;
    ctx.fillText(clipped, W / 2, footY);
  }
};

export default renderSingAlongFrame;
